import React, { useState, useEffect } from 'react';
import { Modal, ModalBody, Card, CardBody, Label, Input } from 'reactstrap';
import metaMask from '../../../assets/images/nft/newrecord.png';
import ActionButton from '../../../Components/Common/ActionButton';
import { FormGrid, RequiredNote, TextField } from '../../../Components/Form';

const PARTICIPATION = {
  PRESENT: false,
  DELEGATE: true,
};

const PARTICIPATION_LABEL = {
  [PARTICIPATION.PRESENT]: 'Present',
  [PARTICIPATION.DELEGATE]: 'Delegate',
};

const isValidEmail = value =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

// Confirm popup for Invitation Evaluation "Save & Confirm".
//  - Option box: Present / Delegate.
//  - When Delegate is chosen, a Participant Information card appears with
//    Name and Email (email-format validated).
//  - Submit -> onConfirm({ mode, name, email }).
//  - Cancel -> onClose; nothing is saved.
const ConfirmInvitationModal = ({ show, submitting, onConfirm, onClose }) => {
  const [mode, setMode] = useState(PARTICIPATION.PRESENT);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (show) {
      setMode(PARTICIPATION.PRESENT);
      setName('');
      setEmail('');
      setJobTitle('');
      setTouched({});
      setSubmitted(false);
    }
  }, [show]);

  // An error shows once the field was left, or after a failed submit.
  const showError = field => submitted || touched[field];
  const touch = field => setTouched(prev => ({ ...prev, [field]: true }));

  const isDelegate = mode === PARTICIPATION.DELEGATE;
  const nameInvalid = isDelegate && !name.trim();
  const emailInvalid = isDelegate && !isValidEmail(email);
  const jobTitleInvalid = isDelegate && !jobTitle.trim();

  const handleSubmit = () => {
    if (isDelegate && (nameInvalid || emailInvalid || jobTitleInvalid)) {
      setSubmitted(true);
      return;
    }
    onConfirm({
      mode,
      name: name.trim(),
      email: email.trim(),
      jobTitle: jobTitle.trim(),
    });
  };

  return (
    <Modal isOpen={show} toggle={onClose} centered data-bs-backdrop='static'>
      <ModalBody className='p-4 text-center'>
        <img src={metaMask} alt='' height='65' className='mb-2 pb-2' />
        <h5 className='fw-semibold mb-2'>Confirm this invitation?</h5>
        <p className='text-muted fs-13 mb-3'>
          Once confirmed, the invitation evaluation is submitted and cannot be
          changed. Choose who will take part below to continue.
        </p>

        <div className='d-flex flex-wrap gap-2 mb-3 text-start'>
          {[PARTICIPATION.PRESENT, PARTICIPATION.DELEGATE].map(opt => (
            <div
              key={String(opt)}
              className='form-check card-radio flex-grow-1'
            >
              <Input
                id={`invitation-participation-${PARTICIPATION_LABEL[
                  opt
                ].toLowerCase()}`}
                name='invitation-participation-mode'
                type='radio'
                className='form-check-input'
                checked={mode === opt}
                onChange={() => setMode(opt)}
              />
              <Label
                className='form-check-label d-flex align-items-center gap-2'
                for={`invitation-participation-${PARTICIPATION_LABEL[
                  opt
                ].toLowerCase()}`}
              >
                <i
                  className={`${
                    opt === PARTICIPATION.PRESENT
                      ? 'ri-user-line'
                      : 'ri-user-shared-line'
                  } fs-16 align-middle`}
                ></i>
                <span className='fw-medium'>{PARTICIPATION_LABEL[opt]}</span>
              </Label>
            </div>
          ))}
        </div>

        {isDelegate && (
          <Card className='border shadow-none mb-3 text-start'>
            <CardBody>
              <h6 className='fw-semibold mb-1'>Participant Information</h6>
              <p className='text-muted fs-13 mb-3'>
                Enter the information of the participant to be delegated
              </p>
              <RequiredNote />
              <FormGrid columns={1}>
                <TextField
                  label='Name'
                  name='participantName'
                  required
                  placeholder='e.g. Budi Santoso'
                  value={name}
                  error={
                    showError('name') && nameInvalid
                      ? 'Name is required'
                      : undefined
                  }
                  onChange={e => setName(e.target.value)}
                  onBlur={() => touch('name')}
                />
                <TextField
                  label='Email'
                  name='participantEmail'
                  type='email'
                  required
                  placeholder='name@company.com'
                  value={email}
                  error={
                    showError('email') && emailInvalid
                      ? 'Enter a valid email address'
                      : undefined
                  }
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => touch('email')}
                />
                <TextField
                  label='Job title'
                  name='participantJobTitle'
                  required
                  placeholder='e.g. Procurement Analyst'
                  value={jobTitle}
                  error={
                    showError('jobTitle') && jobTitleInvalid
                      ? 'Job title is required'
                      : undefined
                  }
                  onChange={e => setJobTitle(e.target.value)}
                  onBlur={() => touch('jobTitle')}
                />
              </FormGrid>
            </CardBody>
          </Card>
        )}

        <div className='d-flex flex-wrap align-items-center justify-content-between gap-2'>
          <ActionButton
            variant='secondary'
            icon='ri-close-line'
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            icon='ri-send-plane-line'
            onClick={handleSubmit}
            loading={submitting}
          >
            Confirm invitation
          </ActionButton>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ConfirmInvitationModal;
